/**
 * 统一分析事件上报封装。
 * 同时发送 Clarity + GA4 事件，确保所有关键行为被追踪。
 *
 * GA4 事件设计规范：
 * - 事件名：snake_case（GA4 推荐格式）
 * - 参数：附带业务数据（搜索词、实体名、页面等）
 * - 所有事件都可通过 Tag Manager 进一步配置
 *
 * Clarity 事件：保持现有中文格式不变。
 */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// ==================== 基础工具 ====================

function callClarity(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.clarity !== "function") return;
  try { window.clarity(...args); } catch { /* 静默 */ }
}

function callGtag(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try { window.gtag(...args); } catch { /* 静默 */ }
}

// ==================== Clarity 专用 ====================

export function clarityIdentify(userId: number | string, friendlyName?: string): void {
  callClarity("identify", String(userId), undefined, undefined, friendlyName);
}

export function claritySetTag(key: string, value: string | string[]): void {
  callClarity("set", key, value);
}

export function clarityUpgrade(reason: string): void {
  callClarity("upgrade", reason);
}

// ==================== GA4 专用 ====================

/** 发送 GA4 自定义事件（带参数） */
function ga4Event(eventName: string, params?: Record<string, unknown>): void {
  callGtag("event", eventName, params);
}

// ==================== 统一事件上报 ====================

/**
 * 上报自定义事件（Clarity + GA4 双通道）。
 * Clarity: 中文事件名，出现在 Smart events / Filters / Recordings
 * GA4: snake_case 事件名 + 业务参数，出现在 GA4 Events / Tag Manager
 */
export function clarityEvent(name: string): void {
  callClarity("event", name);
}

/**
 * 统一事件上报：同时发送 Clarity + GA4。
 * 推荐使用此函数替代单独的 clarityEvent 调用。
 *
 * @param eventName - GA4 事件名（snake_case）
 * @param params - GA4 事件参数
 * @param clarityName - Clarity 事件名（可选，默认同 eventName）
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
  clarityName?: string,
): void {
  // Clarity 事件
  callClarity("event", clarityName || eventName);

  // GA4 事件
  ga4Event(eventName, {
    ...params,
    // 自动附加页面信息
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}

/**
 * 上报事件并在投递完成后(或超时后)跳转。
 *
 * 解决"点完立刻 window.location.href / router.push 导致 GA4 事件丢失"问题:
 *   gtag('event', ...) 内部把请求放入 fetch/beacon 队列异步发送,
 *   如果调用方立刻触发页面卸载,队列会被浏览器中止,事件丢失。
 *
 * 实现:
 *   - transport: 'beacon' 强制 navigator.sendBeacon,即使页面卸载也能完成投递
 *   - event_callback 等 GA 确认投递后才跳转
 *   - 300ms 兜底超时: GA 离线/脚本未加载时也不会卡死跳转
 */
export function trackEventThenNavigate(
  eventName: string,
  params: Record<string, unknown> | undefined,
  clarityName: string | undefined,
  navigate: () => void,
): void {
  // Clarity 同步发(本身不会拦截卸载)
  callClarity("event", clarityName || eventName);

  let navigated = false;
  const go = () => {
    if (navigated) return;
    navigated = true;
    navigate();
  };

  // 兜底: 300ms 内无论 GA 是否回调都跳
  const fallback = setTimeout(go, 300);

  callGtag("event", eventName, {
    ...params,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    transport_type: "beacon",
    event_callback: () => {
      clearTimeout(fallback);
      go();
    },
  });

  // GA 未加载时 callGtag 直接 return,fallback 仍会触发
}

// ==================== 预定义事件（规范化事件名和参数） ====================

/** 搜索动作 */
export function trackSearch(query: string, source: "submit" | "suggestion" | "page" = "submit") {
  trackEvent("search", {
    search_term: query,
    search_source: source,
  }, "搜索");
}

/** 搜索动作 + 跳转 (修复 trackSearch 紧接 location.href 丢事件) */
export function trackSearchAndGo(query: string, href: string, source: "submit" | "page" = "submit") {
  trackEventThenNavigate(
    "search",
    { search_term: query, search_source: source },
    "搜索",
    () => { window.location.href = href; },
  );
}

/** 搜索建议点击 */
export function trackSearchSuggestion(query: string, position?: number) {
  trackEvent("select_search_suggestion", {
    search_term: query,
    suggestion_position: position,
  }, "搜索建议点击");
}

/**
 * 候选词点击 + 跳转:
 * 同时发 search(source=suggestion) + select_search_suggestion 两个事件。
 *
 * 为什么发两个:
 *   - search: 让"搜索量"大盘包含通过候选直达的用户(否则候选越好用,搜索量越少看着越冷清);
 *     通过 search_source=suggestion 区分提交搜索 vs 候选直达
 *   - select_search_suggestion: 单独看候选利用率 / 候选位置点击分布
 *
 * 两个事件共享 search_term 维度,在 GA4 里互不抵消、可分别筛选。
 */
export function trackSearchSuggestionAndGo(query: string, position: number | undefined, href: string) {
  // search 事件(走 beacon,不阻塞)
  callClarity("event", "搜索");
  callGtag("event", "search", {
    search_term: query,
    search_source: "suggestion",
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    transport_type: "beacon",
  });

  // 主事件 + 跳转
  trackEventThenNavigate(
    "select_search_suggestion",
    { search_term: query, suggestion_position: position },
    "搜索建议点击",
    () => { window.location.href = href; },
  );
}

/** 点击词条（热搜列表中的话题行） */
export function trackTopicClick(topicName: string, rank?: number, tab?: string, hotIndex?: number) {
  trackEvent("topic_click", {
    topic_name: topicName,
    topic_rank: rank,
    list_tab: tab,
    hot_index: hotIndex,
  }, "打开词条抽屉");
}

/** 点击微博搜索（外部链接） */
export function trackWeiboSearch(source: string, entityName?: string) {
  trackEvent("click_weibo_search", {
    click_source: source,
    entity_name: entityName,
  }, "点击微博");
}

/** 点击百科（外部链接） */
export function trackBaikeClick(source: string, entityName?: string) {
  trackEvent("click_baike", {
    click_source: source,
    entity_name: entityName,
  }, "点击百科");
}

/** 关注/取关 */
export function trackFollow(keyword: string, action: "follow" | "unfollow", entityType?: string) {
  trackEvent(action === "follow" ? "subscribe_keyword" : "unsubscribe_keyword", {
    keyword,
    entity_type: entityType || "keyword",
  }, action === "follow" ? "订阅关键词" : "取消订阅关键词");

  if (action === "follow") {
    clarityUpgrade("订阅关键词");
  }
}

/** 点击名人 */
export function trackCelebrityClick(name: string, source?: string) {
  trackEvent("click_celebrity", {
    celebrity_name: name,
    click_source: source,
  }, "点击名人");
}

/** 点击城市 */
export function trackCityClick(name: string) {
  trackEvent("click_city", {
    city_name: name,
  }, "点击城市");
}

/** AI 分析进入可视区并成功加载；这是曝光，不等同于用户主动使用。 */
export function trackAiAnalysisImpression(entityName: string, entityType: string, cached: boolean) {
  trackEvent("ai_analysis_impression", {
    entity_name: entityName,
    entity_type: entityType,
    from_cache: cached,
  }, "AI分析曝光");
}

/** 用户主动要求刷新 AI 分析。 */
export function trackAiAnalysisRefresh(entityName: string, entityType: string) {
  trackEvent("ai_analysis_refresh", {
    entity_name: entityName,
    entity_type: entityType,
  }, "刷新AI分析");
}

/** AI 事件解读成功呈现；同一 brief 由调用方去重。 */
export function trackAiBriefImpression(topicName: string, sourceCount: number, status: string) {
  trackEvent("ai_brief_impression", {
    topic_name: topicName,
    source_count: sourceCount,
    status,
  }, "事件解读曝光");
}

/** 请求生成 AI 事件解读 */
export function trackAiBriefRequest(topicName: string) {
  trackEvent("ai_brief_request", {
    topic_name: topicName,
  }, "请求事件解读");
}

/** 点击事件解读来源链接 */
export function trackAiBriefSourceClick(topicName: string, siteName: string) {
  trackEvent("ai_brief_source_click", {
    topic_name: topicName,
    site_name: siteName,
  }, "点击解读来源");
}

/** 切换时间/Tab */
export function trackTabSwitch(tab: string, context?: string) {
  trackEvent("tab_switch", {
    tab_name: tab,
    context,
  }, "切换Tab_" + tab);
}

/** 切换时光机时间 */
export function trackTimeTravel(timeDesc: string) {
  trackEvent("time_travel", {
    time_description: timeDesc,
  }, "切换时光机时间");
}

/** 登录/注册 */
export function trackAuth(action: "login" | "register") {
  const eventName = action === "register" ? "sign_up" : "login";
  trackEvent(eventName, { method: "password" }, action === "login" ? "登录" : "注册");
  if (action === "register") {
    clarityUpgrade("注册");
  }
}

/** 发送 AI 消息 */
export function trackAiChat() {
  trackEvent("ai_chat_send", {}, "发送AI消息");
  clarityUpgrade("AI对话");
}

/** 点击 Affinity 胶囊 */
export function trackAffinityClick(entityName: string, entityType: string) {
  trackEvent("click_affinity", {
    entity_name: entityName,
    entity_type: entityType,
  }, "点击affinity");
}

/** 城市榜筛选 */
export function trackCityFilter(filterType: string) {
  trackEvent("city_filter", {
    filter_type: filterType,
  }, "城市榜筛选:" + filterType);
}
